import {conf} from "../config";
import {ICountryService, Store, StoreDelta} from "../components/store-locator/models";

declare global {
  interface Window {
    initMap: () => void;
    NY_COUNTRY_SERVICE: ICountryService;
  }
}

class StoreService {
  // =========================
  // 🔹 PROPERTIES
  // =========================

  countryCode?: string = "";
  stores: Store[] = [];

  private STORES_KEY: string = "NY_STORES";
  private STORES_UPDATE_KEY: string = "NY_STORES_UPDATE";

  constructor() {
    this.countryCode = this.getCountryCode();
  }

  // =========================
  // 🔹 LOAD STORES
  // =========================

  async loadStores(): Promise<Store[]> {
    const savedStores = this.loadStoresFromStorage();
    if (savedStores.length <= 0) {
      return this.loadStoresFromApi();
    }
    return this.loadStoreDeltaForApi(savedStores);
  }

  async loadStoresFromApi(): Promise<Store[]> {
    const res = await fetch(`${conf.storeServiceUrl}/store/query?limit=10000`);
    if (res) {
      const json = await res.json();
      const stores = json.items.map((it: any) => new Store(it));
      this.saveStores(stores);
      return stores;
    }
    return [];
  }

  async loadStoreDeltaForApi(currentStores: Store[]): Promise<Store[]> {
    const res = await fetch(`${conf.storeServiceUrl}/store/query?modifiedAfter=${this.getLastUpdate().toISOString()}`);
    if (res) {
      const json = await res.json();
      const storeDelta = new StoreDelta(json);
      const updatedStores = this.mergeStoresWithDelta(currentStores, storeDelta);
      this.saveStores(updatedStores);
      return updatedStores;
    }
    return [];
  }

  // =========================
  // 🔹 STORAGE HANDLING
  // =========================

  loadStoresFromStorage(): Store[] {
    const res = window.localStorage.getItem(this.STORES_KEY);
    return res ? JSON.parse(res) : [];
  }

  getLastUpdate(): Date {
    const res = window.localStorage.getItem(this.STORES_UPDATE_KEY);
    return res ? new Date(JSON.parse(res)) : new Date();
  }

  saveStores(stores: Store[]): Store[] {
    window.localStorage.setItem(this.STORES_KEY, JSON.stringify(stores));
    window.localStorage.setItem(this.STORES_UPDATE_KEY, JSON.stringify(new Date()));
    return stores;
  }

  mergeStoresWithDelta(currentStores: Store[], delta: StoreDelta): Store[] {
    const updatedStores = [...currentStores];
    for (const item of delta.items) {
      const exists = currentStores.findIndex(it => it.id === item.id);
      exists <= -1 ? updatedStores.push(item) : updatedStores[exists] = item;
    }
    return updatedStores.filter(store =>
      !(delta.deletions?.find(it => it.store_id === store.id))
    );
  }

  // =========================
  // 🔹 URL + COUNTRY
  // =========================

  getCountryCode(): string {
    const url = window.location.href;
    let cc = conf.defaultLang;
    const index = url.indexOf('newyorker.de/');

    if (index > 0) {
      const path = url.substring(index + 'newyorker.de/'.length);
      const fragments = path.split('/');
      if (fragments.length > 0 && /^[a-z]{2}$/.test(fragments[0])) {
        cc = fragments[0];
      }
    }

    return cc;
  }

  // =========================
  // 🔹 CUSTOMER STORE HANDLING
  // =========================

  subscribeForMyStoreId(callback: Function) {
    const _window = window as any;
    if (_window?.NY_CUSTOMER_SERVICE) {
      _window.NY_CUSTOMER_SERVICE.subscribeForCustomer("NY_STORE_LOCATOR", (customer: any) => {
        callback(customer?.profile.storeId);
      });
    }
  }

  saveMyStore(storeId?: string): Promise<any> {
    const _window = window as any;
    if (_window?.NY_CUSTOMER_SERVICE) {
      return _window.NY_CUSTOMER_SERVICE.updateProfile({storeId});
    }
    return Promise.reject("no customer service initialized in window");
  }

  storeCallback(store: Store, id: string) {
    const options = {
      detail: id ? store : "",
      bubbles: true,
      composed: true
    };
    dispatchEvent(new CustomEvent('myStore', options));
  }
}

export const storeService = new StoreService();
